import React, { useEffect, useRef, useState } from "react";

import InfiniteScroll from "react-infinite-scroll-component";
import { isMobile } from "react-device-detect";

import { Loading } from "./index.jsx";
import StatePanel from "./ui/StatePanel";
import axios from "axios";
import { requireResponse } from "@/core/errors/requireResponse";

const CustomInfiniteScroll = ({
  className = "",
  fetchParams,
  fetchUrl,
  fetchLimit,

  data = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isInitialLoading = false,
  isFromUsersPage,
  isLeadsPage,
  responsive = true,
  children,
  onResultsLoaded,
  firstPageOnly = false,
  maxItemsCount,
  height,
  scrollableTarget,
  gateUntilSearch = false,
  minSearchLength = 0,
  // Opt-in (Leads). react-infinite-scroll-component only pages on a scroll
  // event, so a first batch that doesn't fill a wide screen never loads more,
  // and its loader only shows between a scroll trigger and the response — a
  // first load or filter change painted nothing at all. `fillViewport` keeps
  // fetching until the list runs past the fold and shows a spinner whenever a
  // fetch is in flight.
  fillViewport = false,
}) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  // A failed load used to fall through to an empty list, so the page said
  // "No results" while the server was down. These flags show a retry instead.
  const [loadFailed, setLoadFailed] = useState(false);
  const [nextBatchFailed, setNextBatchFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Monotonic counter — incremented each time a new load starts.
  // Each load captures its own ID; if the counter has moved on by the time
  // the response arrives the result is stale and discarded.
  // This correctly handles React StrictMode (double-invoke) and rapid
  // fetchParams changes without needing AbortController support in axios 0.19.
  const loadCountRef = useRef(0);

  // Stable ref so async callbacks always call the latest onResultsLoaded
  const onResultsLoadedRef = useRef(onResultsLoaded);
  useEffect(() => { onResultsLoadedRef.current = onResultsLoaded; });

  // itemsRef tracks current items so fetchNextBatch never has a stale offset
  const itemsRef = useRef([]);
  // Set synchronously, so a scroll trigger and the viewport fill landing in
  // the same tick can't both request the same offset.
  const batchInFlightRef = useRef(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (fetchNextPage) return;

    loadCountRef.current += 1;
    const myId = loadCountRef.current;

    itemsRef.current = [];
    setItems([]);
    setHasMore(true);
    setIsLoading(true);
    setLoadFailed(false);
    setNextBatchFailed(false);

    const trimmedQuery = String(fetchParams?.query ?? "").trim();

    // Privacy gate: when the caller restricts this directory (no CRM visibility)
    // and no search term is present, skip the fetch entirely so the full CRM list
    // never loads. Typing a search clears the gate and a normal fetch runs.
    if (gateUntilSearch && !trimmedQuery) {
      onResultsLoadedRef.current?.([], 0);
      setHasMore(false);
      setIsLoading(false);
      return;
    }

    // Minimum search length: don't query on 1..minSearchLength-1 characters
    // (avoids noisy partial searches). An empty query is unaffected here — it
    // falls through to the normal load (or the privacy gate above).
    if (trimmedQuery.length > 0 && trimmedQuery.length < minSearchLength) {
      onResultsLoadedRef.current?.([], 0);
      setHasMore(false);
      setIsLoading(false);
      return;
    }

    const load = async () => {
      try {
        const params = {
          ...fetchParams,
          paging: { offset: 0, limit: fetchLimit },
        };

        console.log("[RC] FETCH START id=" + myId, fetchUrl, params);

        // A 5xx / dropped connection resolves undefined (global interceptor);
        // throw so the catch below shows the error state, not an empty list.
        const response = requireResponse(
          await axios.post(fetchUrl, params, { timeout: 60000 })
        );

        console.log("[RC] FETCH DONE id=" + myId, "current=" + loadCountRef.current, "status=", response?.status, "items=", response?.data?.items?.length);

        if (loadCountRef.current !== myId) return; // stale — discard

        const responseItems = response?.data?.items ?? [];
        const responseTotal = response?.data?.total ?? responseItems.length;
        // Resolve hasMore robustly: endpoints like /api/users/search omit the
        // flag entirely. Trust an explicit server value if present; otherwise
        // treat a full page as "probably more" so infinite-scroll keeps firing
        // (audit: Clients/Merchants/Experts only showed 15 rows).
        const responseHasMore = response?.data?.hasMore;
        const resolvedHasMore =
          responseHasMore !== undefined
            ? responseHasMore
            : responseItems.length >= fetchLimit;
        const reallyHasMore = responseItems.length < fetchLimit ? false : resolvedHasMore;

        onResultsLoadedRef.current?.(responseItems, responseTotal);
        itemsRef.current = responseItems;
        setItems(responseItems);
        setHasMore(firstPageOnly || responseItems.length >= (maxItemsCount ?? Infinity)
          ? false
          : reallyHasMore);
      } catch (err) {
        console.log("[RC] FETCH ERROR id=" + myId, "current=" + loadCountRef.current, "code=", err?.code, "status=", err?.response?.status, "msg=", err?.response?.data?.message ?? err?.message);
        if (loadCountRef.current !== myId) return; // stale — discard
        if (
          err?.code === "ERR_CANCELED" ||
          err?.name === "AbortError" ||
          err?.name === "CanceledError"
        ) return;

        console.error(
          "[RC] InfiniteScroll fetch failed:",
          err?.response?.status,
          err?.response?.data?.message ?? err?.message
        );
        onResultsLoadedRef.current?.([], 0); // always update count, even on error
        setHasMore(false);
        setLoadFailed(true);
      } finally {
        if (loadCountRef.current === myId) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      // Invalidate this load so its result is discarded if still in flight
      loadCountRef.current += 1;
    };
  }, [JSON.stringify(fetchParams), fetchUrl, gateUntilSearch, minSearchLength, reloadKey]);

  const fetchNextBatch = async () => {
    if (isLoading || batchInFlightRef.current) return;
    batchInFlightRef.current = true;
    // A filter change mid-flight starts a new load; this batch's rows belong
    // to the old filter and must not be appended to the new list.
    const myId = loadCountRef.current;
    setIsLoading(true);
    setNextBatchFailed(false);

    try {
      const params = {
        ...fetchParams,
        paging: { offset: itemsRef.current.length, limit: fetchLimit },
      };

      console.debug("[RC] next batch POST offset:", itemsRef.current.length);

      const response = requireResponse(
        await axios.post(fetchUrl, params, { timeout: 30000 })
      );
      if (loadCountRef.current !== myId) return; // stale — discard
      const responseItems = response?.data?.items ?? [];
      // Same hasMore resolution as the initial load — see above.
      const responseHasMore = response?.data?.hasMore;
      const resolvedHasMore =
        responseHasMore !== undefined
          ? responseHasMore
          : responseItems.length >= fetchLimit;
      const reallyHasMore = responseItems.length < fetchLimit ? false : resolvedHasMore;
      const newItems = [...itemsRef.current, ...responseItems];

      itemsRef.current = newItems;
      setItems(newItems);
      setHasMore(firstPageOnly || newItems.length >= (maxItemsCount ?? Infinity)
        ? false
        : reallyHasMore);
    } catch (err) {
      if (loadCountRef.current !== myId) return;
      if (
        err?.code === "ERR_CANCELED" ||
        err?.name === "AbortError" ||
        err?.name === "CanceledError"
      ) return;
      console.error("[RC] next batch failed:", err?.response?.status, err?.message);
      // Keep the rows already shown; stop auto-paging and offer a retry rather
      // than letting the list look complete.
      setHasMore(false);
      setNextBatchFailed(true);
    } finally {
      batchInFlightRef.current = false;
      // A newer load owns isLoading now; clearing it would hide its spinner.
      if (loadCountRef.current === myId) setIsLoading(false);
    }
  };

  // Keep paging while the end of the list is still on screen. Runs after each
  // batch lands, so it stops as soon as the list reaches past the fold.
  useEffect(() => {
    if (!fillViewport || fetchNextPage || isLoading || !hasMore || nextBatchFailed) return;
    if (!items.length) return;
    const sentinel = sentinelRef.current;
    if (sentinel && sentinel.getBoundingClientRect().top <= window.innerHeight) {
      fetchNextBatch();
    }
  });

  const showLoadError = !fetchNextPage && loadFailed && items.length === 0;
  const showNextBatchError = !fetchNextPage && nextBatchFailed;

  return (
    <InfiniteScroll
      className={className}
      style={{
        overflow:
          scrollableTarget === "scrollableDiv"
            ? "auto"
            : isFromUsersPage
            ? !isLeadsPage && "auto"
            : "visible",
        paddingLeft: isMobile ? "15px" : "",
        paddingRight: isMobile ? "15px" : "",
        overflowX: isMobile ? "hidden" : "",
      }}
      dataLength={items.length}
      next={fetchNextPage ? fetchNextPage : fetchNextBatch}
      hasMore={fetchNextPage ? hasNextPage : hasMore}
      loader={fillViewport ? null : <Loading type="table" showText={items.length === 0} />}
      height={height}
      scrollableTarget={scrollableTarget}
    >
      {showLoadError ? (
        <StatePanel
          state="error"
          compact
          title="Couldn't load this list"
          description="Something went wrong on our side — try again."
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : (
        children({
          items: items.length ? items : data,
          isLoading: fetchNextPage
            ? isInitialLoading || isFetchingNextPage
            : isLoading,
          hasMore: fetchNextPage ? hasNextPage : hasMore,
        })
      )}
      {fillViewport && isLoading && !showLoadError && (
        <Loading type="table" showText={items.length === 0} />
      )}
      {fillViewport && <div ref={sentinelRef} aria-hidden="true" />}
      {showNextBatchError && (
        <StatePanel
          state="error"
          compact
          title="Couldn't load more"
          description={null}
          onRetry={() => {
            setHasMore(true);
            fetchNextBatch();
          }}
        />
      )}
    </InfiniteScroll>
  );
};

export default CustomInfiniteScroll;
